import { PROJECT } from './config.generated';

/**
 * Store availability per platform (project.config.json → stores). Single source for every "is the app in the
 * store?" decision: landing badges, platform texts, JSON-LD, Smart App Banner of landing and web app.
 * Flip a platform to "live" in project.config.json (then `npm run gen:config`) once its store release is public.
 */
export type StorePlatform = 'ios' | 'android';
export type StoreState = 'live' | 'soon';

/** Display order everywhere (badges, texts). */
export const STORE_PLATFORMS: readonly StorePlatform[] = ['ios', 'android'];

/** Widened on purpose so callers can compare against both states without literal-type narrowing. */
export const STORE_STATE: Readonly<Record<StorePlatform, StoreState>> = PROJECT.stores;

export const storeLive = (p: StorePlatform): boolean => STORE_STATE[p] === 'live';
export const liveStorePlatforms = (): StorePlatform[] => STORE_PLATFORMS.filter(storeLive);
export const soonStorePlatforms = (): StorePlatform[] => STORE_PLATFORMS.filter((p) => !storeLive(p));

/**
 * Public store pages. The iOS app is only offered in the German App Store (App Store Connect availability
 * "nur Deutschland"), so the link always names the /de/ storefront, also on English pages.
 */
export const STORE_URLS: Readonly<Record<StorePlatform, string>> = {
  ios: `https://apps.apple.com/de/app/${PROJECT.slug}/id${PROJECT.ids.appleAppId}`,
  android: `https://play.google.com/store/apps/details?id=${PROJECT.ids.playPackage}`,
};

/**
 * Content of `<meta name="apple-itunes-app">` (Safari Smart App Banner), or null while the iOS app is not public.
 * No app-argument: the pages carrying the banner are not app routes (deep links are only /einladung and /mieter).
 */
export const appleSmartBannerContent = (): string | null =>
  storeLive('ios') ? `app-id=${PROJECT.ids.appleAppId}` : null;
